import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { CarePlanStore, LiveGoalWrite } from '../../care-plan.store';
import { CarePlanAnalytics } from '../../analytics/care-plan-analytics';

/** Case goal associations: linked pills with removal and an inline link selector. */
@Component({
  selector: 'bn-case-goal-links',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Select],
  templateUrl: './case-goal-links.component.html',
  styleUrl: './case-goal-links.component.scss',
})
export class CaseGoalLinksComponent {
  readonly caseId = input.required<string>();
  readonly store = input.required<CarePlanStore>();
  readonly manageGoals = output<void>();
  private readonly analytics = inject(CarePlanAnalytics);
  readonly selectedGoalId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  protected readonly linkedGoals = computed(() => {
    const linked = new Set(
      this.store()
        .cases()
        .find((item) => item.id === this.caseId())?.goalIds ?? [],
    );
    return this.store()
      .goals()
      .filter((goal) => linked.has(goal.id))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  });
  protected readonly linkOptions = computed(() => {
    const linked = new Set(this.linkedGoals().map((goal) => goal.id));
    return [
      ...this.store()
        .activeGoals()
        .filter((goal) => !linked.has(goal.id))
        .map((goal) => ({ label: goal.displayText, value: goal.id })),
      ...(this.store().activeGoals().length < 3
        ? [{ label: 'Capture a new goal…', value: '__capture__' }]
        : []),
    ];
  });

  onLinkGoal(goalId: string | null): void {
    this.selectedGoalId.set(goalId);
    if (!goalId || this.saving() || !this.store().canManageGoals()) return;
    if (goalId === '__capture__') {
      this.manageGoals.emit();
      setTimeout(() => this.selectedGoalId.set(null), 0);
      return;
    }
    void this.changeLink({ kind: 'link', goalId, caseId: this.caseId() });
  }

  unlinkGoal(goalId: string): void {
    if (this.saving() || !this.store().canManageGoals()) return;
    void this.changeLink({ kind: 'unlink', goalId, caseId: this.caseId() });
  }

  private async changeLink(
    change: Extract<LiveGoalWrite, { kind: 'link' | 'unlink' }>,
  ): Promise<void> {
    const clientGuid = this.store().sessionGuid();
    this.saving.set(true);
    this.error.set(null);
    try {
      if (this.store().isLive()) {
        await this.store().saveLiveGoals([change], () =>
          this.track(change, clientGuid),
        );
      } else {
        if (change.kind === 'link')
          this.store().linkGoal(change.caseId, change.goalId);
        else this.store().unlinkGoal(change.caseId, change.goalId);
        this.track(change, clientGuid);
      }
    } catch (error) {
      this.error.set(
        error instanceof Error
          ? error.message
          : 'The goal association could not be saved.',
      );
    } finally {
      this.selectedGoalId.set(null);
      this.saving.set(false);
    }
  }

  private track(
    change: Extract<LiveGoalWrite, { kind: 'link' | 'unlink' }>,
    clientGuid: string | null,
  ): void {
    if (change.kind === 'link')
      this.analytics.caseGoalLinked(change.caseId, change.goalId, clientGuid);
    else
      this.analytics.caseGoalUnlinked(change.caseId, change.goalId, clientGuid);
  }
}
